import React, { useState, useEffect, useCallback } from 'react';
import SocialActions from '../../components/SocialActions/SocialActions';
import ContentEditorModal from '../../components/ContentEditorModal/ContentEditorModal';
import { useAuth } from '../../contexts/AuthContext';
import { toEthiopianDate, englishMonths } from '../../utils/dateConverter';
import { useLocation } from 'react-router-dom';

const months = [
    { english: "Meskerem", amharic: "መስከረም" },
    { english: "Tikimt", amharic: "ጥቅምት" },
    { english: "Hidar", amharic: "ኅዳር" },
    { english: "Tahsas", amharic: "ታኅሣሥ" },
    { english: "Tir", amharic: "ጥር" },
    { english: "Yekatit", amharic: "የካቲት" },
    { english: "Megabit", amharic: "መጋቢት" },
    { english: "Miazia", amharic: "ሚያዝያ" },
    { english: "Ginbot", amharic: "ግንቦት" },
    { english: "Sene", amharic: "ሰኔ" },
    { english: "Hamle", amharic: "ሐምሌ" },
    { english: "Nehase", amharic: "ነሐሴ" },
    { english: "Pagume", amharic: "ጳጉሜ" }
];

const getTodayEthiopian = () => {
    const eth = toEthiopianDate(new Date());
    return {
        month: englishMonths[eth.ecMonth - 1],
        day: eth.ecDay.toString()
    };
};

const Gitsawe = () => {
    const { user } = useAuth();
    const location = useLocation();
    const navState = location.state || {};
    const today = getTodayEthiopian();
    const [selectedMonth, setSelectedMonth] = useState(navState.month || today.month);
    const [selectedDay, setSelectedDay] = useState(navState.day ? navState.day.toString() : today.day);
    const [monthData, setMonthData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dayData, setDayData] = useState(null);

    // Editor Modal state
    const [editorOpen, setEditorOpen] = useState(false);
    const [isNewContent, setIsNewContent] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Search functionality
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});

    const isInitialLoad = React.useRef(true);
    const apiURL = process.env.REACT_APP_API_URL || '/api';

    // Load month data directly from MongoDB
    const loadMonthData = useCallback(async (monthToLoad) => {
        if (!monthToLoad) return;
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${apiURL}/content?type=gitsawe&month=${monthToLoad}`);
            if (!res.ok) throw new Error(`HTTP error ${res.status}`);
            const json = await res.json();
            if (json.success && Array.isArray(json.data)) {
                const mappedDays = json.data.map(item => ({
                    id: item.day || item.itemId,
                    _id: item._id,
                    title: item.title,
                    content: item.content
                }));
                setMonthData(mappedDays);
                setLoading(false);
                return;
            }
        } catch (fetchErr) {
            console.error('Failed to load Gitsawe from DB:', fetchErr);
            setError('መረጃውን ከዳታቤዝ መጫን አልተቻለም። እባክዎ እንደገና ይሞክሩ።');
            setMonthData([]);
        } finally {
            setLoading(false);
        }
    }, [apiURL]);

    useEffect(() => {
        if (!selectedMonth) {
            setMonthData([]);
            setSelectedDay('');
            setDayData(null);
            return;
        }

        setDayData(null);
        if (!isInitialLoad.current) {
            setSelectedDay('');
        }
        loadMonthData(selectedMonth);
        isInitialLoad.current = false;
    }, [selectedMonth, loadMonthData]);

    useEffect(() => {
        if (selectedDay && Array.isArray(monthData) && monthData.length > 0) {
            const foundDay = monthData.find(item => item.id === parseInt(selectedDay, 10));
            setDayData(foundDay || null);
        } else {
            setDayData(null);
        }
    }, [selectedDay, monthData]);

    // Search in DB
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await fetch(`${apiURL}/content?type=gitsawe&search=${encodeURIComponent(searchTerm)}`);
                if (res.ok) {
                    const json = await res.json();
                    if (json.success && Array.isArray(json.data)) {
                        setSearchResults(json.data.map(d => ({
                            month: d.month,
                            dayId: d.day || d.itemId,
                            title: d.title,
                            snippet: Array.isArray(d.content) && d.content[0]?.main
                                ? d.content[0].main.substring(0, 100) + '...'
                                : 'የዕለቱ ንባብ'
                        })));
                    }
                }
            } catch (err) {
                console.error('Search error:', err);
            } finally {
                setSearching(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm, apiURL]);

    const handleMonthChange = (e) => setSelectedMonth(e.target.value);
    const handleDayChange = (e) => setSelectedDay(e.target.value);

    const toggleSection = (idx) => {
        setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const getDaysForMonth = (m) => {
        if (!m) return [];
        return m === 'Pagume' ? Array.from({ length: 6 }, (_, i) => i + 1) : Array.from({ length: 30 }, (_, i) => i + 1);
    };

    const handleFirstDay = () => setSelectedDay('1');
    const handleLastDay = () => setSelectedDay(selectedMonth === 'Pagume' ? '5' : '30');

    const handlePrev = () => {
        const cur = parseInt(selectedDay, 10);
        if (cur > 1) setSelectedDay((cur - 1).toString());
    };

    const handleNext = () => {
        const cur = parseInt(selectedDay, 10);
        const max = selectedMonth === 'Pagume' ? 6 : 30;
        if (cur < max) setSelectedDay((cur + 1).toString());
    };

    const handleSearchResultClick = (res) => {
        setSelectedMonth(res.month);
        setSelectedDay(res.dayId.toString());
        setSearchTerm('');
    };

    const canEditContent = user?.canEdit || user?.canUpdate || user?.isAdmin;
    const canInsertContent = user?.canInsert || user?.isAdmin;

    return (
        <div className="page-container gitsawe-theme max-w-7xl mx-auto p-4 sm:p-6">
            <div className="page-header text-center mb-6">
                <h1 className="page-main-title text-3xl font-extrabold text-gray-900 mb-2">📖 መጽሐፈ ግጻዌ</h1>
                <p className="page-subtitle text-gray-600">Daily Orthodox Readings and Liturgical Lectionary</p>
                <div className="mt-2 flex items-center justify-center gap-2">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">
                        ⚡ ከመረጃ ቋት (MongoDB) የተገኘ
                    </span>
                    {canEditContent && (
                        <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium flex items-center gap-1">
                            <span>👤</span> የተፈቀደ አርታዒ ({user?.email})
                        </span>
                    )}
                </div>
            </div>

            <div className="controls-container bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label htmlFor="search-input" className="block text-xs font-bold text-gray-700 mb-1">🔍 ፈልግ</label>
                        <input
                            id="search-input"
                            type="text"
                            placeholder="በርዕስ ወይም ጥቅስ ይፈልጉ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    <div>
                        <label htmlFor="month-select" className="block text-xs font-bold text-gray-700 mb-1">📅 ወር ይምረጡ</label>
                        <select
                            id="month-select"
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                        >
                            <option value="">-- ወር ይምረጡ --</option>
                            {months.map(m => (
                                <option key={m.english} value={m.english}>{m.amharic} ({m.english})</option>
                            ))}
                        </select>
                    </div>
                    {selectedMonth && (
                        <div>
                            <label htmlFor="day-select" className="block text-xs font-bold text-gray-700 mb-1">🗓️ ቀን ይምረጡ</label>
                            <select
                                id="day-select"
                                value={selectedDay}
                                onChange={handleDayChange}
                                className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                            >
                                <option value="">-- ቀን ይምረጡ --</option>
                                {getDaysForMonth(selectedMonth).map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {canInsertContent && (
                    <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                        <button
                            onClick={() => {
                                setIsNewContent(true);
                                setEditingItem(null);
                                setEditorOpen(true);
                            }}
                            className="px-3.5 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-semibold rounded-lg shadow transition flex items-center gap-1.5"
                        >
                            <span>➕</span> አዲስ የንባብ ክፍል ወደ DB አስገባ
                        </button>
                    </div>
                )}
            </div>

            {loading && <div className="text-center py-8 text-gray-500">ይዘቱ ከመረጃ ቋት እየተጫነ ነው...</div>}
            {error && <div className="text-center py-8 text-red-500">{error}</div>}

            {searchTerm && (
                <div className="bg-white rounded-xl shadow p-4 mb-6 border border-gray-200">
                    <h3 className="font-bold text-base mb-3">የፍለጋ ውጤቶች {searching ? '(እየፈለገ ነው...)' : `(${searchResults.length})`}</h3>
                    <div className="space-y-2">
                        {searchResults.map((r, i) => (
                            <div
                                key={i}
                                onClick={() => handleSearchResultClick(r)}
                                className="p-3 border rounded-lg hover:bg-blue-50 cursor-pointer transition text-sm flex items-center justify-between"
                            >
                                <div>
                                    <span className="font-bold text-blue-700">{r.title}</span>
                                    <span className="text-xs text-gray-500 ml-2">({r.month} - {r.dayId})</span>
                                    <p className="text-xs text-gray-600 mt-0.5">{r.snippet}</p>
                                </div>
                                <span className="text-xs text-blue-500">ይመልከቱ →</span>
                            </div>
                        ))}
                        {!searching && searchResults.length === 0 && (
                            <p className="text-xs text-gray-500 py-2">ምንም ውጤት አልተገኘም</p>
                        )}
                    </div>
                </div>
            )}

            {!loading && dayData && (
                <div className="content-display space-y-4">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-xl border border-blue-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">{dayData.title}</h2>
                            <p className="text-sm text-blue-600 font-medium">
                                {months.find(m => m.english === selectedMonth)?.amharic || selectedMonth} {selectedDay}
                            </p>
                        </div>

                        {canEditContent && (
                            <button
                                onClick={() => {
                                    setIsNewContent(false);
                                    setEditingItem(dayData);
                                    setEditorOpen(true);
                                }}
                                className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold shadow transition flex items-center gap-1.5"
                            >
                                <span>✏️</span> ይህን ንባብ አርትዕ (Edit in DB)
                            </button>
                        )}
                    </div>

                    <div className="space-y-4">
                        {Array.isArray(dayData.content) && dayData.content.map((sec, idx) => (
                            <div key={idx} className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 font-bold flex items-center gap-2 text-base">
                                    <span>📖</span> {sec.title || `ክፍል ${idx + 1}`}
                                </div>
                                <div className="p-4 sm:p-6">
                                    <div className="text-gray-800 leading-relaxed text-sm sm:text-base whitespace-pre-line">
                                        {expandedSections[idx] || (sec.main && sec.main.length <= 250)
                                            ? sec.main
                                            : `${sec.main ? sec.main.substring(0, 250) : ''}...`}
                                    </div>
                                    {sec.main && sec.main.length > 250 && (
                                        <button
                                            onClick={() => toggleSection(idx)}
                                            className="mt-3 text-xs font-bold text-blue-600 hover:text-blue-800 transition"
                                        >
                                            {expandedSections[idx] ? 'ሰብስብ ▲' : 'ሙሉውን አንብብ ▼'}
                                        </button>
                                    )}

                                    <div className="mt-4 pt-3 border-t border-gray-100">
                                        <SocialActions
                                            contentId={`gitsawe_${selectedMonth}_${selectedDay}_${idx}`}
                                            contentType="gitsawe"
                                            title={`${dayData.title} - ${sec.title}`}
                                            content={sec.main}
                                            showBookmark={true}
                                            showShare={true}
                                            showLike={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-between items-center pt-4">
                        <button
                            onClick={handlePrev}
                            disabled={parseInt(selectedDay, 10) <= 1}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium disabled:opacity-40 transition"
                        >
                            ← ቀዳሚ ቀን
                        </button>
                        <div className="flex gap-2">
                            <button onClick={handleFirstDay} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs">
                                መጀመሪያ
                            </button>
                            <button onClick={handleLastDay} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs">
                                መጨረሻ
                            </button>
                        </div>
                        <button
                            onClick={handleNext}
                            disabled={parseInt(selectedDay, 10) >= (selectedMonth === 'Pagume' ? 6 : 30)}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium disabled:opacity-40 transition"
                        >
                            ቀጣይ ቀን →
                        </button>
                    </div>
                </div>
            )}

            <ContentEditorModal
                isOpen={editorOpen}
                onClose={() => setEditorOpen(false)}
                contentType="gitsawe"
                month={selectedMonth}
                day={selectedDay}
                initialData={editingItem}
                isNew={isNewContent}
                onSaved={() => {
                    loadMonthData(selectedMonth);
                }}
            />
        </div>
    );
};

export default Gitsawe;
